/* eslint-disable @typescript-eslint/no-shadow */
import type { CSSInterpolation } from '@emotion/serialize';
import type { PopperProps } from '@mui/base/Popper';
import type {
	AutocompleteGroupedOption,
	AutocompleteValue,
	UseAutocompleteProps,
} from '@mui/base/useAutocomplete';
import { useAutocomplete } from '@mui/base/useAutocomplete';
import type { FilterOptionsState } from '@mui/material';
import { createFilterOptions, useControlled, useForkRef } from '@mui/material';
import type {
	ComponentPropsWithoutRef,
	ComponentPropsWithRef,
	HTMLAttributes,
	Key,
	ReactNode,
	Ref,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
	AnimatedPopover,
	HighlightText,
	notchedPopperModifier,
	sameWidthPopperModifier,
} from '@adflow/frontend/ui-common';
import { theme, typography } from '@adflow/frontend/ui-theme';

import type { InputStatus } from '../Inputs';
import { selectorStyles } from '../Selectors';
import {
	limitGroupedOptions,
	limitOptions,
} from '../use-autocomplete-helpers/limit-options';

import type { HubAutocompleteRenderInputProps } from './HubAutocompleteRenderInput';
import { hubAutocompleteDefaultRenderInput } from './HubAutocompleteRenderInput';

export interface HubAutocompleteProps<
	T,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false,
>
	extends
		InputStatus,
		Pick<
			HubAutocompleteRenderInputProps<T, DisableClearable, FreeSolo>,
			| keyof InputStatus
			| 'inputRef'
			| 'placeholder'
			| 'startAdornment'
			| 'endAdornment'
		>,
		Pick<
			UseAutocompleteProps<T, false, DisableClearable, FreeSolo>,
			| 'options'
			| 'getOptionLabel'
			| 'getOptionKey'
			| 'getOptionDisabled'
			| 'isOptionEqualToValue'
			| 'value'
			| 'inputValue'
			| 'onInputChange'
			| 'open'
			| 'onOpen'
			| 'onClose'
			| 'filterOptions'
			| 'groupBy'
			| 'disableClearable'
			| 'freeSolo'
			| 'blurOnSelect'
			| 'autoComplete'
			| 'autoSelect'
			| 'autoHighlight'
		> {
	onChange: (
		value: AutocompleteValue<T, false, DisableClearable, FreeSolo>
	) => void;
	popoverRef?: Ref<HTMLDivElement>;
	testid?: string;

	search?: string;
	/**
	 * Called with the input's value as a user types and reset to '' on close. This is so
	 * that the next time the user opens the dropdown, they see the full list of
	 * options. Without doing this the user would only see the currently selected
	 * option in the dropdown until they clear the input manually.
	 */
	setSearch?: (value: string) => void;

	/**
	 * The maximum number of options to show in the list. This is used to prevent
	 * performance problems from rendering large lists. Set to
	 * `Number.POSITIVE_INFINITY` to show all options.
	 */
	maxListSize?: number;
	canSearchForMore?: boolean;

	isLoading?: boolean;
	className?: string;
	styles?: {
		root?: CSSInterpolation;
		listbox?: CSSInterpolation;
		input?: CSSInterpolation;
	};
	slotProps?: {
		root?: Partial<ComponentPropsWithoutRef<'div'>>;
		listbox?: Partial<ComponentPropsWithRef<'ul'>>;
		input?: Partial<ComponentPropsWithoutRef<'input'>>;
	};

	renderInput?: (
		props: HubAutocompleteRenderInputProps<T, DisableClearable>
	) => ReactNode;
	renderGroup?: (props: HubAutocompleteRenderGroupProps) => ReactNode;
	renderOption?: (props: HubAutocompleteRenderOptionProps<T>) => ReactNode;
	hideDropdownWhenEmpty?: boolean;
	emptyMessage?: string;
	dropdownTopContainer?: ReactNode;
	dropdownBottomContainer?: ReactNode;
	popperProps?: Partial<PopperProps>;
	getExample?: (option: T) => string;
	highlightExample?: boolean;
}

function defaultFilterOptions<T>(options: T[], state: FilterOptionsState<T>) {
	return createFilterOptions<T>()(options, state);
}

/**
 * - Select a single option from a list of options.
 * - The options can be filtered by typing in the input.
 */
export function HubAutocomplete<
	T,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false,
>({
	// HubAutocompleteRenderInputProps
	error,
	archived,
	readOnly,
	disabled,
	inputRef,
	placeholder,
	startAdornment,
	endAdornment,

	// UseAutocompleteProps
	options,
	getOptionLabel = (option) => '' + option,
	getOptionKey = getOptionLabel,
	getExample,
	highlightExample = false,
	getOptionDisabled,
	isOptionEqualToValue,
	value,
	onChange,
	inputValue: propInputValue,
	onInputChange,
	open,
	onOpen,
	onClose,
	filterOptions = defaultFilterOptions,
	groupBy,
	disableClearable,
	freeSolo,
	blurOnSelect,
	autoComplete,
	autoSelect,
	autoHighlight,

	// other
	popoverRef,
	testid,
	maxListSize = 50,
	canSearchForMore,

	search: propsSearch,
	setSearch: propsSetSearch,

	isLoading,
	className,
	styles,
	slotProps,

	renderInput = hubAutocompleteDefaultRenderInput,
	renderGroup = hubAutocompleteDefaultRenderGroup,
	renderOption = hubAutocompleteDefaultRenderOption,
	hideDropdownWhenEmpty = false,
	emptyMessage,
	dropdownTopContainer,
	dropdownBottomContainer,
	popperProps = HubAutocompleteDefaultPopperProps,
}: HubAutocompleteProps<T, DisableClearable, FreeSolo>) {
	const { t } = useTranslation();

	const [search, localSetSearch] = useControlled({
		controlled: propsSearch,
		default: '',
		name: 'HubAutocomplete',
		state: 'search',
	});
	const setSearch = (search: string) => {
		localSetSearch(search);
		propsSetSearch?.(search);
	};

	const {
		getRootProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		groupedOptions,
		anchorEl,
		setAnchorEl,
		popupOpen: _popupOpen,
		getPopupIndicatorProps,
		getClearProps,
		inputValue,
		focused,
	} = useAutocomplete<T, false, DisableClearable, FreeSolo>({
		options,
		getOptionLabel: (...args) => getOptionLabel(...args) ?? '',
		getOptionKey,
		getOptionDisabled,
		isOptionEqualToValue,

		value,
		onChange: (_event, value) => {
			onChange(value);
		},

		disabled,

		// Prevent popover from opening when the input is read-only.
		open: readOnly ? false : open,
		onOpen,
		onClose: (event, reason) => {
			onClose?.(event, reason);
			setSearch('');
		},

		inputValue: propInputValue,
		onInputChange: (event, value, reason) => {
			onInputChange?.(event, value, reason);
			if (reason === 'input') {
				setSearch(value);
			}
		},

		filterOptions: (options, state) =>
			filterOptions(options, { ...state, inputValue: search }),
		groupBy,
		blurOnSelect,
		disableClearable,
		freeSolo,
		autoComplete,
		autoSelect,
		autoHighlight,
	});

	const renderLimitedNotice = () => (
		<li css={selectorStyles.emptyListItem}>
			{t('Type to search for more results')}
		</li>
	);

	const renderOptionInternal = (option: T, index: number) => {
		let optionProps = getOptionProps({ option, index });

		if (getOptionDisabled?.(option)) {
			optionProps = { ...optionProps, onClick: undefined };
		}

		return renderOption({
			optionProps,
			label: getOptionLabel(option),
			option,
			search,
			example: getExample?.(option),
			highlightExample,
		});
	};

	const renderOptions = (options: T[]) =>
		options.map((option, index) => renderOptionInternal(option, index));

	const renderGroups = (groups: AutocompleteGroupedOption<T>[]) =>
		groups.map(({ key, group, options, index }) =>
			renderGroup({
				key,
				group,
				children: options.map((option2, index2) =>
					renderOptionInternal(option2, index + index2)
				),
			})
		);

	const popupOpen =
		_popupOpen && (groupedOptions.length > 0 || !hideDropdownWhenEmpty);

	const listboxProps = getListboxProps();
	const listBoxRef = (
		listboxProps as HTMLAttributes<HTMLUListElement> & {
			ref?: Ref<HTMLUListElement>;
		}
	)?.ref;
	const listboxRef = useForkRef(listBoxRef, slotProps?.listbox?.ref);

	return (
		<div
			{...getRootProps()}
			{...slotProps?.root}
			className={className}
			css={styles?.root}
		>
			{renderInput({
				error,
				archived,
				readOnly,
				disabled,
				inputRef,
				placeholder,
				startAdornment,
				endAdornment,

				disableClearable,

				inputProps: { ...getInputProps(), ...slotProps?.input },
				setAnchorEl,
				getClearProps,
				getPopupIndicatorProps,
				popupOpen,
				focused,
				inputValue,

				styles,
				testid,
			})}
			<AnimatedPopover
				ref={popoverRef}
				anchorEl={anchorEl}
				open={popupOpen}
				PopperProps={popperProps}
				css={selectorStyles.popover()}
				data-testid={`${testid}-popover`}
			>
				<ul
					{...listboxProps}
					{...slotProps?.listbox}
					ref={listboxRef}
					css={[
						selectorStyles.list,
						{
							// The element that gets `getListboxProps` must also be the
							// element that scrolls, so that the selected option can be
							// scrolled into view.
							maxHeight: '40vh',
							overflow: 'auto',
						},
						styles?.listbox,
					]}
				>
					{dropdownTopContainer}

					{groupedOptions.length <= 0 ? (
						isLoading ? (
							<li css={[selectorStyles.emptyListItem, typography.emptySmall]}>
								{t('Loading')}
							</li>
						) : (
							<li css={selectorStyles.emptyListItem}>
								{emptyMessage ?? t('No results')}
							</li>
						)
					) : groupBy ? (
						(() => {
							const { limitedGroups, limited } = limitGroupedOptions(
								groupedOptions as AutocompleteGroupedOption<T>[],
								maxListSize
							);

							return (
								<>
									{renderGroups(limitedGroups)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>
							);
						})()
					) : (
						(() => {
							const { limitedOptions, limited } = limitOptions(
								groupedOptions as T[],
								maxListSize
							);

							return (
								<>
									{renderOptions(limitedOptions)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>
							);
						})()
					)}

					{dropdownBottomContainer}
				</ul>
			</AnimatedPopover>
		</div>
	);
}

export const HubAutocompleteBasePopperProps: Partial<PopperProps> = {
	placement: 'bottom-start',
	modifiers: [
		{ name: 'offset', options: { offset: [0, -2] } },
		notchedPopperModifier({ notchDepth: 1 }),
	],
};

export const HubAutocompleteDefaultPopperProps: Partial<PopperProps> = {
	...HubAutocompleteBasePopperProps,
	modifiers: [
		...(HubAutocompleteBasePopperProps.modifiers ?? []),
		sameWidthPopperModifier,
	],
};

export interface HubAutocompleteRenderGroupProps {
	key: Key;
	group: string;
	children?: ReactNode;
}

export function hubAutocompleteDefaultRenderGroup({
	key,
	group,
	children,
}: HubAutocompleteRenderGroupProps) {
	return (
		<li key={key}>
			<div
				css={[
					typography.regular,
					{ color: theme.colors.text.secondary, padding: '0.5rem 0.75rem' },
				]}
			>
				{group}
			</div>
			<ul css={{ '& > li': { paddingLeft: '1.5rem' } }}>{children}</ul>
		</li>
	);
}

export interface HubAutocompleteRenderOptionProps<T> {
	optionProps: HTMLAttributes<HTMLLIElement> & { key: string | number };
	label: string;
	option: T;
	search: string;
	example?: ReactNode | string;
	key?: Key;
	highlightExample?: boolean;
}

export function hubAutocompleteDefaultRenderOption<T>({
	optionProps,
	label,
	search,
	example,
	key,
	highlightExample,
}: HubAutocompleteRenderOptionProps<T>) {
	return (
		<li
			{...optionProps}
			key={optionProps.key ?? key}
			css={selectorStyles.listItem}
		>
			<HighlightText text={label} highlight={search} lineClamp />
			{typeof example === 'string' && highlightExample ? (
				<HighlightText
					text={example}
					highlight={search}
					css={{ color: theme.colors.text.faint, marginLeft: '0.75rem' }}
				/>
			) : example ? (
				<div css={{ color: theme.colors.text.faint, marginLeft: '0.75rem' }}>
					{example}
				</div>
			) : null}
		</li>
	);
}
